// Provider-target resolution for `gundi:reference` annotations with
// `target: "provider"`. See:
//   docs/superpowers/specs/2026-07-31-reference-data-config-ui-design.md §3
//     ("Provider resolution")
//   src/api/referenceOptions.ts (reused here for the per-provider fetches)
//
// For target: "provider", the field's options come from the integration(s)
// on the other side of the connection(s) in which the configured integration
// is the *destination* — not from the integration being configured itself.
// A provider only counts if its integration *type* registered the named
// reference action. Providers lacking it are skipped; if none has it, the
// caller (ReferenceSelectWidget) falls back to free text.
import { createAxiosInstance, getQueryString, filter_page_size } from "./apiUtils";
import { fetchReferenceOptions, ReferenceDataResponse, ReferenceOption } from "./referenceOptions";

// --- Per-provider integration-details cache ---------------------------------
// Mirrors referenceOptions.ts's cache shape: cache the in-flight promise
// immediately (dedupes concurrent lookups for the same provider), then keep
// the resolved value cached for the TTL. Evict on failure so a transient
// error doesn't wedge a provider out of consideration for 5 minutes.
type DetailsCacheEntry = { expiresAt: number; promise: Promise<any> };
const detailsCache = new Map<string, DetailsCacheEntry>();

const PROVIDER_DETAILS_TTL_MS = 300 * 1000;
const PROVIDER_DETAILS_INFLIGHT_TTL_MS = 15000;

export function clearProviderTargetsCache() { detailsCache.clear(); } // tests

function fetchIntegrationDetailsCached(token: string, providerId: string): Promise<any> {
    const hit = detailsCache.get(providerId);
    if (hit && hit.expiresAt > Date.now()) return hit.promise;
    const promise = (async () => {
        const axiosInstance = await createAxiosInstance(token);
        const resp = await axiosInstance.get(`/v2/integrations/${encodeURIComponent(providerId)}/`);
        const data = resp.data;
        detailsCache.set(providerId, { expiresAt: Date.now() + PROVIDER_DETAILS_TTL_MS, promise: Promise.resolve(data) });
        return data;
    })();
    detailsCache.set(providerId, { expiresAt: Date.now() + PROVIDER_DETAILS_INFLIGHT_TTL_MS, promise });
    promise.catch(() => { if (detailsCache.get(providerId)?.promise === promise) detailsCache.delete(providerId); });
    return promise;
}

/** True iff the integration's type registered `actionValue` as a reference action. */
function supportsReferenceAction(integrationDetails: any, actionValue: string): boolean {
    const actions = integrationDetails?.type?.actions;
    if (!Array.isArray(actions)) return false;
    return actions.some((a: any) => a && a.value === actionValue && a.type === "reference");
}

/** Resolve the provider integration ids that (a) are on the other side of a
 * connection where `destinationIntegrationId` is the destination, and (b)
 * whose integration type registers `actionValue` as a reference action.
 * Fetches connections in a single, generously-limited page (per the spec:
 * "resolve the connections in which the integration is a destination").
 * A single provider whose details can't be fetched is skipped (best-effort);
 * a failure fetching the connections list itself propagates to the caller. */
export async function resolveProviderTargets(
    token: string,
    destinationIntegrationId: string,
    actionValue: string,
): Promise<string[]> {
    const axiosInstance = await createAxiosInstance(token);
    const query_string = getQueryString({ destination_id: destinationIntegrationId });
    const resp = await axiosInstance.get(
        `/v2/connections/?pager=limit&limit=${filter_page_size}&${query_string}`,
    );
    const connections: any[] = resp.data?.results ?? [];

    const providerIds = Array.from(
        new Set(
            connections
                .map((connection) => connection?.provider?.id)
                .filter((id: unknown): id is string => typeof id === "string" && id.length > 0),
        ),
    );

    const checked = await Promise.all(
        providerIds.map(async (providerId) => {
            try {
                const details = await fetchIntegrationDetailsCached(token, providerId);
                return supportsReferenceAction(details, actionValue) ? providerId : null;
            } catch {
                // Best-effort: a provider we can't inspect is skipped, not fatal.
                return null;
            }
        }),
    );

    return checked.filter((id): id is string => id !== null);
}

/** Provider-target equivalent of fetchReferenceOptions: resolves the matching
 * providers, fans the reference action out to each (via fetchReferenceOptions,
 * so per-provider results are TTL-cached and in-flight-deduped exactly like
 * the self-target path), and unions the results deduped by option value
 * (first occurrence wins). `providerCount` is the number of providers that
 * matched (not the number that successfully returned options) — the caller
 * uses providerCount === 0 to decide whether to fall back to free text. */
export async function fetchProviderReferenceOptions(
    token: string,
    destinationIntegrationId: string,
    actionValue: string,
    params: Record<string, unknown>,
): Promise<ReferenceDataResponse & { providerCount: number }> {
    const providerIds = await resolveProviderTargets(token, destinationIntegrationId, actionValue);

    if (providerIds.length === 0) {
        return { options: [], cache_ttl_seconds: 300, truncated: false, providerCount: 0 };
    }

    const settled = await Promise.allSettled(
        providerIds.map((providerId) => fetchReferenceOptions(token, providerId, actionValue, params)),
    );

    const successes = settled.filter(
        (r): r is PromiseFulfilledResult<ReferenceDataResponse> => r.status === "fulfilled",
    );

    if (successes.length === 0) {
        // Every provider fetch failed: rethrow (the first failure) so the
        // widget shows the same error degrade as the self-target path.
        throw (settled[0] as PromiseRejectedResult).reason;
    }

    const byValue = new Map<string, ReferenceOption>();
    let truncated = false;
    let minTtlSeconds: number | undefined;
    for (const { value: response } of successes) {
        truncated = truncated || !!response.truncated;
        const ttl = response.cache_ttl_seconds ?? 300;
        minTtlSeconds = minTtlSeconds === undefined ? ttl : Math.min(minTtlSeconds, ttl);
        for (const option of response.options ?? []) {
            if (!byValue.has(option.value)) byValue.set(option.value, option);
        }
    }

    return {
        options: Array.from(byValue.values()),
        cache_ttl_seconds: minTtlSeconds ?? 300,
        truncated,
        providerCount: providerIds.length,
    };
}
